import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/articles';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-white/90 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/751042991eee80e3d0f5ce87d83ca658/ecd9c2a833b45c83e224d75a48614e57.png"
              alt="JoyaGoo Coupon Logo"
              className="h-9 w-auto object-contain"
            />
            <span className="font-bold text-lg text-gray-900 whitespace-nowrap hidden sm:block">JoyaGoo Coupon</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap">Home</Link>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shop by Category
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer text-left"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-orange-400`}></i>
                      </div>
                      <span>{cat.name}</span>
                      <i className="ri-arrow-right-s-line ml-auto text-gray-400"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link to="/joyagoo-coupon-code" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap">Coupons</Link>
            <Link to="/joyagoo-review" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap">Reviews</Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://repsw2c.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-semibold text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              joyagoo coupon
            </a>
            <a
              href="https://repsw2c.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-gray-700 cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-gray-700 py-2 cursor-pointer">Home</Link>
          <div>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-gray-700 py-2 cursor-pointer w-full"
            >
              Shop by Category
              <i className={`ri-arrow-down-s-line ml-1 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="pl-4 space-y-1 mt-1">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="block w-full text-left text-sm text-gray-600 py-2 hover:text-orange-500 cursor-pointer"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link to="/joyagoo-coupon-code" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-gray-700 py-2 cursor-pointer">Coupons</Link>
          <div className="flex gap-3 pt-2">
            <a href="https://repsw2c.com/" target="_blank" rel="noopener noreferrer" className="flex-1 text-center px-4 py-2 text-sm font-semibold text-orange-600 border border-orange-200 rounded-lg cursor-pointer whitespace-nowrap">joyagoo coupon</a>
            <a href="https://repsw2c.com/" target="_blank" rel="noopener noreferrer" className="flex-1 text-center px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg cursor-pointer whitespace-nowrap">Buy It Now</a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
